import { useMemo } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { boschGray } from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import { getJsonPath, toFiniteNumber } from './jsonPath';
import { DEFAULT_SERIES_COLORS } from './chartData';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(theme => ({
  chartBox: {
    padding: theme.spacing(1.5, 1, 0.5, 1),
    minHeight: 280,
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
}));

export type ApiPieChartProps = {
  title?: string;
  url?: string;
  inlineJson?: string;
  /** Path to the array of slices. Each row contributes one slice. */
  path?: string;
  /** Dot path on each row pointing at the slice label. */
  labelKey: string;
  /** Dot path on each row pointing at the slice numeric value. */
  valueKey: string;
  /** When true, render as a donut (with a hole in the middle).
   *  Visually lighter — useful when sitting next to other widgets. */
  donut?: boolean;
  refreshSeconds?: number;
};

/**
 * Pie / donut chart for distributions: components per kind, deploys
 * per environment, incidents per severity, etc.
 *
 * Limit: we cap the number of slices at the colour palette length
 * silently — Recharts will happily render 50 slices with repeating
 * colours, but that's never a useful visualisation. If the admin
 * really has many categories, they should bucket the long tail
 * server-side into an "Other" slice before sending it.
 */
export const ApiPieChart = ({
  title,
  url,
  inlineJson,
  path,
  labelKey,
  valueKey,
  donut = false,
  refreshSeconds,
}: ApiPieChartProps) => {
  const classes = useStyles();
  const { data, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    path,
    refreshSeconds,
  });

  const slices = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .map(row => {
        const labelRaw = getJsonPath(row, labelKey);
        const valueNum = toFiniteNumber(getJsonPath(row, valueKey));
        if (valueNum === undefined) return undefined;
        return {
          name: labelRaw === undefined ? '(unlabelled)' : String(labelRaw),
          value: valueNum,
        };
      })
      .filter((s): s is { name: string; value: number } => !!s);
  }, [data, labelKey, valueKey]);

  return (
    <InfoCard title={title ?? 'Distribution'} noPadding>
      {!url && !inlineJson ? (
        <Typography className={classes.empty}>
          Provide a URL or inline JSON to populate this chart.
        </Typography>
      ) : !Array.isArray(data) && data !== undefined ? (
        <Typography className={classes.empty}>
          Path <code>{path || '(root)'}</code> resolved to a {typeof data},
          not an array.
        </Typography>
      ) : slices.length === 0 ? (
        <Typography className={classes.empty}>
          No slices to draw. Check the label/value keys.
        </Typography>
      ) : (
        <div className={classes.chartBox}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                /* `paddingAngle={1}` gives slices visible borders
                 * even when adjacent colours are similar. */
                paddingAngle={1}
                outerRadius={donut ? 100 : 110}
                innerRadius={donut ? 60 : 0}
                stroke="#FFFFFF"
                strokeWidth={1}
              >
                {slices.map((_, idx) => (
                  <Cell
                    key={idx}
                    fill={
                      DEFAULT_SERIES_COLORS[
                        idx % DEFAULT_SERIES_COLORS.length
                      ]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: `1px solid ${boschGray[85]}`,
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                /* Top legend would steal vertical room from the pie
                 * itself at the chart's typical 260px height. Right
                 * side keeps the pie centered and gives the labels
                 * room to wrap. */
                layout="vertical"
                align="right"
                verticalAlign="middle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </InfoCard>
  );
};
